import React, { useState, useEffect, useRef, useCallback } from "react";
import { createReading, updateReading, searchVocabulary, searchPhrases } from "./adminReadingService";
import "../../styles/content.css";

const defaultForm = {
  title: "",
  content: "",
  difficultyId: "",
  categoryId: "",
  sortOrder: "",
  youtubeUrl: "",
  warmupQuestions: [],
  trueFalseStatements: [],
  discussionQuestions: [],
};

const extractYoutubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([-\w]{11})/);
  return m ? m[1] : null;
};

const extractVimeoId = (url) => {
  if (!url) return null;
  const m = url.match(/vimeo\.com\/(?:video\/)?([0-9]+)/);
  return m ? m[1] : null;
};

const getEmbedUrl = (url) => {
  if (!url) return null;
  const ytId = extractYoutubeId(url);
  if (ytId) return `https://www.youtube.com/embed/${ytId}`;
  const vimeoId = extractVimeoId(url);
  if (vimeoId) return `https://player.vimeo.com/video/${vimeoId}`;
  return null;
};

// Reusable searchable multi-select
const SearchMultiSelect = ({ label, selectedItems, onAdd, onRemove, searchFn, placeholder }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  const handleSearch = useCallback((q) => {
    clearTimeout(debounceRef.current);
    if (!q.trim()) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      const res = await searchFn(q);
      if (res.status === 200 && Array.isArray(res.data)) {
        setResults(res.data);
        setOpen(true);
      }
    }, 300);
  }, [searchFn]);

  useEffect(() => { handleSearch(query); }, [query, handleSearch]);

  useEffect(() => {
    const handler = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedIds = new Set(selectedItems.map((i) => i.id));

  return (
    <div className="form-group" ref={wrapperRef}>
      <label style={{ fontWeight: 600 }}>{label}</label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
        {selectedItems.map((item) => (
          <span key={item.id} style={{ background: "#e0f7fa", color: "#00796b", padding: "3px 10px", borderRadius: 999, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
            {item.label}
            <button type="button" onClick={() => onRemove(item.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#00796b", fontWeight: 700, padding: 0, lineHeight: 1 }}>✕</button>
          </span>
        ))}
      </div>
      <div style={{ position: "relative" }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #ccc", boxSizing: "border-box" }}
        />
        {open && results.length > 0 && (
          <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #ddd", borderRadius: 8, zIndex: 100, maxHeight: 220, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
            {results.map((r) => (
              <div
                key={r.id}
                onClick={() => { if (!selectedIds.has(r.id)) { onAdd(r); } setQuery(""); setOpen(false); }}
                style={{ padding: "8px 14px", cursor: selectedIds.has(r.id) ? "default" : "pointer", background: selectedIds.has(r.id) ? "#f5f5f5" : "#fff", opacity: selectedIds.has(r.id) ? 0.5 : 1 }}
              >
                <div style={{ fontWeight: 600, fontSize: 14 }}>{r.label}</div>
                {r.subLabel && <div style={{ fontSize: 12, color: "#888" }}>{r.subLabel}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ReadingForm = ({ passage, categories, difficulties, onSaved, onCancel }) => {
  const isEdit = !!passage;
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [selectedVocab, setSelectedVocab] = useState([]);
  const [selectedPhrases, setSelectedPhrases] = useState([]);

  useEffect(() => {
    if (passage) {
      setForm({
        title: passage.title || "",
        content: passage.content || "",
        difficultyId: passage.difficultyId ?? (typeof passage.difficulty === "object" ? passage.difficulty?.id : "") ?? "",
        categoryId: passage.categoryId ?? (typeof passage.category === "object" ? passage.category?.id : "") ?? "",
        sortOrder: passage.sortOrder ?? "",
        youtubeUrl: passage.youtubeUrl || "",
        warmupQuestions: passage.warmupQuestions?.map((q) => ({ question: q.question, sortOrder: q.sortOrder })) || [],
        trueFalseStatements: passage.trueFalseStatements?.map((s) => ({ statement: s.statement, correctAnswer: s.correctAnswer, sortOrder: s.sortOrder })) || [],
        discussionQuestions: passage.discussionQuestions?.map((q) => ({ question: q.question, sortOrder: q.sortOrder })) || [],
      });
      setSelectedVocab(passage.vocabularies?.map((v) => ({ id: v.id, label: v.word, subLabel: v.definition })) || []);
      setSelectedPhrases(passage.phrases?.map((p) => ({ id: p.id, label: p.title, subLabel: p.meaning })) || []);
    }
  }, [passage]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const addItem = (section, template) =>
    setForm((f) => ({ ...f, [section]: [...f[section], template] }));

  const removeItem = (section, idx) =>
    setForm((f) => ({ ...f, [section]: f[section].filter((_, i) => i !== idx) }));

  const updateItem = (section, idx, key, value) =>
    setForm((f) => {
      const updated = [...f[section]];
      updated[idx] = { ...updated[idx], [key]: value };
      return { ...f, [section]: updated };
    });

  const handleSubmit = async () => {
    if (!form.title || !form.content) { alert("Title and Content are required"); return; }
    const payload = {
      title: form.title,
      content: form.content,
      difficultyId: form.difficultyId ? Number(form.difficultyId) : null,
      categoryId: form.categoryId ? Number(form.categoryId) : null,
      sortOrder: form.sortOrder !== "" ? Number(form.sortOrder) : null,
      youtubeUrl: form.youtubeUrl || null,
      warmupQuestions: form.warmupQuestions,
      trueFalseStatements: form.trueFalseStatements,
      discussionQuestions: form.discussionQuestions,
      vocabularyIds: selectedVocab.map((v) => v.id),
      phraseIds: selectedPhrases.map((p) => p.id),
    };
    setLoading(true);
    const res = isEdit ? await updateReading(passage.id, payload) : await createReading(payload);
    if (res.status === 200 || res.status === 201) {
      onSaved();
    } else {
      alert(res.message || "Operation failed");
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ width: 700, maxHeight: "90vh", overflowY: "auto" }}>
        <div className="form-card" style={{ margin: 0, boxShadow: "none" }}>
          <h2>{isEdit ? "Edit" : "Create"} Reading Passage</h2>

          <div className="form-group">
            <label>Title *</label>
            <input name="title" placeholder="e.g. The Future of Remote Work" value={form.title} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>Content * (HTML — use &lt;u&gt; for underline, &lt;mark&gt; for highlight)</label>
            <textarea name="content" rows={6} placeholder="Paste HTML content here..." value={form.content} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>YouTube URL</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                name="youtubeUrl"
                type="url"
                placeholder="https://www.youtube.com/watch?v=..."
                value={form.youtubeUrl}
                onChange={handleChange}
                maxLength={500}
                style={{ flex: 1 }}
              />
              {form.youtubeUrl && (
                <button type="button" onClick={() => setForm((f) => ({ ...f, youtubeUrl: "" }))} style={{ padding: "0 10px", cursor: "pointer" }}>✕</button>
              )}
            </div>
            {extractYoutubeId(form.youtubeUrl) && (
              <div style={{ marginTop: 8, display: "inline-block" }}>
                <img
                  src={`https://img.youtube.com/vi/${extractYoutubeId(form.youtubeUrl)}/hqdefault.jpg`}
                  alt="thumbnail"
                  style={{ height: 90, borderRadius: 8, border: "1px solid #ddd" }}
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              </div>
            )}
            {extractVimeoId(form.youtubeUrl) && (
              <div style={{ marginTop: 8, fontSize: 13, color: "#019095", fontWeight: 600 }}>
                ✓ Vimeo URL detected
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Difficulty</label>
              <select name="difficultyId" value={form.difficultyId} onChange={handleChange}>
                <option value="">— Select —</option>
                {difficulties.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Category</label>
              <select name="categoryId" value={form.categoryId} onChange={handleChange}>
                <option value="">— Select —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Sort Order</label>
              <input name="sortOrder" type="number" placeholder="e.g. 1" value={form.sortOrder} onChange={handleChange} />
            </div>
          </div>

          {/* Warm-up Questions */}
          <div className="form-group">
            <label style={{ fontWeight: 600 }}>Warm-up Questions</label>
            {form.warmupQuestions.map((q, i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                <input style={{ flex: 1 }} placeholder={`Question ${i + 1}`} value={q.question} onChange={(e) => updateItem("warmupQuestions", i, "question", e.target.value)} />
                <input type="number" style={{ width: 70 }} placeholder="Order" value={q.sortOrder} onChange={(e) => updateItem("warmupQuestions", i, "sortOrder", Number(e.target.value))} />
                <button type="button" className="delete-btn" onClick={() => removeItem("warmupQuestions", i)}>✕</button>
              </div>
            ))}
            <button type="button" onClick={() => addItem("warmupQuestions", { question: "", sortOrder: form.warmupQuestions.length + 1 })}>+ Add Question</button>
          </div>

          {/* True/False Statements */}
          <div className="form-group">
            <label style={{ fontWeight: 600 }}>True / False Statements</label>
            {form.trueFalseStatements.map((s, i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center" }}>
                <input style={{ flex: 1 }} placeholder={`Statement ${i + 1}`} value={s.statement} onChange={(e) => updateItem("trueFalseStatements", i, "statement", e.target.value)} />
                <select value={String(s.correctAnswer)} onChange={(e) => updateItem("trueFalseStatements", i, "correctAnswer", e.target.value === "true")} style={{ width: 80 }}>
                  <option value="true">True</option>
                  <option value="false">False</option>
                </select>
                <input type="number" style={{ width: 70 }} placeholder="Order" value={s.sortOrder} onChange={(e) => updateItem("trueFalseStatements", i, "sortOrder", Number(e.target.value))} />
                <button type="button" className="delete-btn" onClick={() => removeItem("trueFalseStatements", i)}>✕</button>
              </div>
            ))}
            <button type="button" onClick={() => addItem("trueFalseStatements", { statement: "", correctAnswer: true, sortOrder: form.trueFalseStatements.length + 1 })}>+ Add Statement</button>
          </div>

          {/* Discussion Questions */}
          <div className="form-group">
            <label style={{ fontWeight: 600 }}>Discussion Questions</label>
            {form.discussionQuestions.map((q, i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                <input style={{ flex: 1 }} placeholder={`Question ${i + 1}`} value={q.question} onChange={(e) => updateItem("discussionQuestions", i, "question", e.target.value)} />
                <input type="number" style={{ width: 70 }} placeholder="Order" value={q.sortOrder} onChange={(e) => updateItem("discussionQuestions", i, "sortOrder", Number(e.target.value))} />
                <button type="button" className="delete-btn" onClick={() => removeItem("discussionQuestions", i)}>✕</button>
              </div>
            ))}
            <button type="button" onClick={() => addItem("discussionQuestions", { question: "", sortOrder: form.discussionQuestions.length + 1 })}>+ Add Question</button>
          </div>

          {/* Vocabulary searchable multi-select */}
          <SearchMultiSelect
            label="Vocabulary"
            selectedItems={selectedVocab}
            onAdd={(item) => setSelectedVocab((prev) => [...prev, item])}
            onRemove={(id) => setSelectedVocab((prev) => prev.filter((v) => v.id !== id))}
            searchFn={searchVocabulary}
            placeholder="Search vocabulary words..."
          />

          {/* Phrases searchable multi-select */}
          <SearchMultiSelect
            label="Phrases"
            selectedItems={selectedPhrases}
            onAdd={(item) => setSelectedPhrases((prev) => [...prev, item])}
            onRemove={(id) => setSelectedPhrases((prev) => prev.filter((p) => p.id !== id))}
            searchFn={searchPhrases}
            placeholder="Search phrases..."
          />

          <div className="form-actions">
            <button onClick={handleSubmit} disabled={loading}>{loading ? "Saving..." : isEdit ? "Update" : "Create"}</button>
            <button onClick={onCancel} disabled={loading}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReadingForm;
